import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  CalendarDaysIcon,
  MapPinIcon,
  MinusIcon,
  PlusIcon,
  ShieldCheckIcon,
  TicketIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getPublicEventTicketStorefront, publicEventTicketCalculateSummary, publicEventTicketCheckout, publicEventTicketCompanyRequest } from '../../../services/eventApi';
import { extractPresignedUrlFromResponse, getPresignedUrl } from '../../../services/fileApi';
import LoadingSpinner from '../../Common/LoadingSpinner';
import { formatTicketMoney, formatTicketPrice } from '../../../utils/eventTicketMoney';

const catalogPrice = formatTicketPrice;
const money = formatTicketMoney;

const formatEventDateRange = (startDate, endDate) => {
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  const fmt = (d) => (d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-GB') : '');
  const s = fmt(start);
  const e = fmt(end);
  if (s && e && s !== e) return `${s} – ${e}`;
  return s || e || '';
};

async function resolveFileUrl(fileId) {
  const id = String(fileId || '').trim();
  if (!id) return null;
  const res = await getPresignedUrl(id);
  return extractPresignedUrlFromResponse(res) || null;
}

function TicketQuantityStepper({
  value,
  onDecrement,
  onIncrement,
  disabled,
  label,
  atMax,
  atMin,
}) {
  return (
    <div className="flex flex-col items-end gap-1">
      <div
        className={`inline-flex items-center overflow-hidden rounded-xl border bg-white shadow-sm ${
          disabled ? 'border-stone-200 opacity-60' : 'border-stone-300'
        }`}
      >
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          disabled={disabled || atMin}
          onClick={onDecrement}
          className="flex h-10 w-10 items-center justify-center text-stone-600 transition hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <MinusIcon className="h-4 w-4" aria-hidden />
        </button>
        <span
          className="min-w-[2.5rem] border-x border-stone-200 px-2 text-center text-sm font-semibold tabular-nums text-stone-900"
          aria-live="polite"
        >
          {value}
        </span>
        <button
          type="button"
          aria-label={`Increase ${label}`}
          disabled={disabled || atMax}
          onClick={onIncrement}
          className="flex h-10 w-10 items-center justify-center text-stone-600 transition hover:bg-stone-100 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <PlusIcon className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {atMax && !disabled ? (
        <span className="text-[10px] font-medium text-amber-800">Max reached</span>
      ) : null}
    </div>
  );
}

function PriceBreakdown({ lineTotal, calculating }) {
  const { total, taxLines, tax, taxPercent, grand, currency, isTaxable } = lineTotal;
  return (
    <div className="rounded-xl bg-stone-50 px-4 py-3 text-sm">
      <div className="flex justify-between text-stone-600">
        <span>Subtotal{lineTotal.count > 0 ? ` (${lineTotal.count} ticket${lineTotal.count === 1 ? '' : 's'})` : ''}</span>
        <span className="tabular-nums">{money(total, currency)}</span>
      </div>
      {isTaxable && taxLines.map((tl, i) => (
        <div key={tl.code || tl.taxCode || i} className="mt-1 flex justify-between border-l-2 border-stone-300 pl-2 text-xs text-stone-500">
          <span>{tl.taxName || tl.name || tl.taxCode || tl.code} ({tl.percentage ?? tl.rate}%)</span>
          <span className="tabular-nums">{money(tl.amount, currency)}</span>
        </div>
      ))}
      {isTaxable && (
        <div className="mt-1 flex justify-between text-xs font-medium text-stone-600">
          <span>Total taxes{taxPercent ? ` (${taxPercent}%)` : ''}</span>
          <span className="tabular-nums">{money(tax, currency)}</span>
        </div>
      )}
      <div className="mt-2 flex justify-between border-t border-stone-200 pt-2 text-base font-bold text-stone-900">
        <span>Total{calculating ? ' …' : ''}</span>
        <span className="tabular-nums">{money(grand, currency)}</span>
      </div>
    </div>
  );
}

const HIGHLIGHTS = [
  { icon: UserIcon, text: 'No account required' },
  { icon: ShieldCheckIcon, text: 'Secure payment link' },
  { icon: TicketIcon, text: 'Instant order confirmation' },
];

const INPUT_CLS = 'mt-1 w-full rounded-xl border border-stone-300 px-3 py-2.5 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500';

/** Labelled field; required fields are marked with a red asterisk, optional ones say so. */
const Field = ({ label, required = false, hint = '', children }) => (
  <label className="block text-xs font-medium text-stone-600">
    <span className="flex items-baseline justify-between gap-2">
      <span>
        {label}
        {required ? <span className="ml-0.5 text-red-600" aria-hidden="true">*</span> : <span className="ml-1 font-normal text-stone-400">(optional)</span>}
      </span>
      {hint ? <span className="font-normal text-stone-400">{hint}</span> : null}
    </span>
    {children}
  </label>
);

const PublicEventTicketPurchasePage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [qty, setQty] = useState({});
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    companyVatNumber: '',
    companyTin: '',
    billingAddress: '',
  });
  const [companyForm, setCompanyForm] = useState({
    companyName: '',
    coordinatorName: '',
    coordinatorEmail: '',
    coordinatorPhone: '',
    companyVatNumber: '',
    companyTin: '',
    billingAddress: '',
    notes: '',
  });
  const [purchaseMode, setPurchaseMode] = useState('guest');
  const [companyResult, setCompanyResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [heroUrl, setHeroUrl] = useState(null);
  const [mediaUrls, setMediaUrls] = useState({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getPublicEventTicketStorefront(eventId);
        if (!cancelled) setData(res.data?.data);
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.message || 'Ticketing unavailable');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  useEffect(() => {
    const fileId = String(data?.publicPage?.photoFileId || data?.event?.bannerFileId || '').trim();
    if (!fileId) {
      setHeroUrl(null);
      return undefined;
    }
    let cancelled = false;
    resolveFileUrl(fileId)
      .then((url) => { if (!cancelled) setHeroUrl(url); })
      .catch(() => { if (!cancelled) setHeroUrl(null); });
    return () => { cancelled = true; };
  }, [data?.publicPage?.photoFileId, data?.event?.bannerFileId]);

  useEffect(() => {
    const page = data?.publicPage || {};
    const ids = [
      ...(page.sponsors || []).map((s) => s.logoFileId),
      ...(page.speakers || []).map((s) => s.photoFileId),
    ].map((id) => String(id || '').trim()).filter(Boolean);
    const unique = [...new Set(ids)];
    if (!unique.length) {
      setMediaUrls({});
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        unique.map(async (id) => {
          try {
            return [id, await resolveFileUrl(id)];
          } catch {
            return [id, null];
          }
        })
      );
      if (!cancelled) {
        setMediaUrls(Object.fromEntries(entries.filter(([, url]) => url)));
      }
    })();
    return () => { cancelled = true; };
  }, [data?.publicPage]);

  const types = data?.ticketTypes || [];
  const page = data?.publicPage || {};
  const event = data?.event || {};
  const salesOpen = String(data?.salesStatus || '').toUpperCase() === 'OPEN';
  const dateLabel = formatEventDateRange(event.startDate, event.endDate);
  const maxAvailable = Math.max(0, Number(data?.inventory?.available ?? 0));
  const totalCapacity = Number(data?.inventory?.totalCapacity || data?.inventory?.sellableCapacity || maxAvailable);

  const totalSelected = useMemo(
    () => types.reduce((sum, t) => sum + (Math.max(0, parseInt(qty[t.id], 10) || 0)), 0),
    [qty, types]
  );

  const remainingPool = Math.max(0, maxAvailable - totalSelected);

  const getQty = useCallback((typeId) => Math.max(0, parseInt(qty[typeId], 10) || 0), [qty]);

  const setQtyForType = useCallback((typeId, nextVal, { silent } = {}) => {
    setQty((prev) => {
      const parsed = Math.max(0, Number.isFinite(nextVal) ? Math.floor(nextVal) : parseInt(nextVal, 10) || 0);
      const others = types.reduce((sum, t) => {
        if (t.id === typeId) return sum;
        return sum + (Math.max(0, parseInt(prev[t.id], 10) || 0));
      }, 0);
      const maxForThis = Math.max(0, maxAvailable - others);
      const clamped = Math.min(parsed, maxForThis);
      if (!silent && parsed > clamped) {
        if (maxAvailable <= 0) {
          toast.error('No tickets available right now');
        } else if (maxForThis === 0) {
          toast.error(`Only ${maxAvailable} ticket(s) available — reduce another selection first`);
        } else {
          toast.error(`You can select up to ${maxForThis} of this ticket (${maxAvailable} total available)`);
        }
      }
      return { ...prev, [typeId]: clamped };
    });
  }, [types, maxAvailable]);

  const adjustQty = useCallback((typeId, delta) => {
    const current = getQty(typeId);
    setQtyForType(typeId, current + delta);
  }, [getQty, setQtyForType]);

  const buildValidatedItems = useCallback(() => {
    const items = types
      .map((t) => ({ ticketTypeId: t.id, quantity: getQty(t.id) }))
      .filter((i) => i.quantity > 0);
    const total = items.reduce((s, i) => s + i.quantity, 0);
    if (!items.length) {
      toast.error('Select at least one ticket');
      return null;
    }
    if (total > maxAvailable) {
      toast.error(`Only ${maxAvailable} ticket(s) available (${total} selected)`);
      return null;
    }
    if (!salesOpen) {
      toast.error('Ticket sales are closed');
      return null;
    }
    return items;
  }, [types, getQty, maxAvailable, salesOpen]);

  const selectedLines = useMemo(
    () => types
      .map((t) => ({ ticketTypeId: t.id, quantity: getQty(t.id) }))
      .filter((l) => l.quantity > 0),
    [types, getQty]
  );

  // Tax is computed server-side from the finance tax configuration (same as staff sales).
  const [summary, setSummary] = useState(null);
  const [calculating, setCalculating] = useState(false);
  useEffect(() => {
    if (!eventId || !selectedLines.length) {
      setSummary(null);
      return undefined;
    }
    let cancelled = false;
    setCalculating(true);
    const timer = setTimeout(() => {
      publicEventTicketCalculateSummary(eventId, { lines: selectedLines })
        .then((res) => { if (!cancelled) setSummary(res.data?.data || null); })
        .catch(() => { if (!cancelled) setSummary(null); })
        .finally(() => { if (!cancelled) setCalculating(false); });
    }, 150);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [eventId, selectedLines]);

  const lineTotal = useMemo(() => {
    let localTotal = 0;
    let count = 0;
    const lines = [];
    types.forEach((t) => {
      const q = getQty(t.id);
      if (q > 0) {
        lines.push({ id: t.id, name: t.name, qty: q, lineAmount: q * Number(t.price || 0), currency: t.currency });
      }
      localTotal += q * Number(t.price || 0);
      count += q;
    });
    const live = count > 0 ? summary : null;
    const total = live?.subTotal ?? localTotal;
    const tax = Number(live?.taxTotal || 0);
    return {
      total,
      tax,
      grand: live?.grandTotal ?? total,
      taxLines: Array.isArray(live?.taxLines) ? live.taxLines : [],
      taxPercent: live?.taxPercent || 0,
      isTaxable: Boolean(live?.isTaxable && tax > 0),
      count,
      currency: live?.currency || data?.inventory?.currency || 'LKR',
      lines,
    };
  }, [types, getQty, data, summary]);

  const checkout = async (e) => {
    e.preventDefault();
    const items = buildValidatedItems();
    if (!items) return;
    setBusy(true);
    try {
      const res = await publicEventTicketCheckout(eventId, { ...form, items });
      const out = res.data?.data;
      setResult(out);
      toast.success('Order created — complete payment');
      if (out?.paymentToken) {
        navigate(`/public/event-ticket-payment/${out.paymentToken}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Checkout failed');
    } finally {
      setBusy(false);
    }
  };

  const submitCompanyRequest = async (e) => {
    e.preventDefault();
    const items = buildValidatedItems();
    if (!items) return;
    setBusy(true);
    try {
      const res = await publicEventTicketCompanyRequest(eventId, { ...companyForm, lines: items });
      const out = res.data?.data;
      setCompanyResult(out);
      toast.success(`Request ${out?.requestNumber || ''} submitted — staff will review`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-stone-100 to-amber-50/30">
        <LoadingSpinner />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-b from-stone-100 to-amber-50/30 p-6 text-center text-slate-600">
        Ticketing is not available for this event.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 via-amber-50/30 to-stone-100 text-slate-900">
      {/* Top bar */}
      <div className="border-b border-stone-200/60 bg-white/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-amber-800/80">SLIM Events</p>
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${
              salesOpen ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-200 text-stone-600'
            }`}
          >
            {salesOpen ? 'Sales open' : 'Sales closed'}
          </span>
        </div>
      </div>

      {/* Hero header */}
      <header className="relative overflow-hidden border-b border-stone-200/80">
        {heroUrl ? (
          <>
            <div className="absolute inset-0">
              <img src={heroUrl} alt="" className="h-full w-full object-cover" aria-hidden />
              <div className="absolute inset-0 bg-gradient-to-r from-stone-950/85 via-stone-900/70 to-stone-900/40" />
            </div>
            <div className="relative mx-auto max-w-6xl px-4 py-10 sm:py-14">
              <h1 className="max-w-3xl font-serif text-3xl tracking-tight text-white sm:text-4xl lg:text-5xl">
                {page.headline || event.name}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-stone-200 sm:text-base">
                {page.description || event.description || 'Purchase tickets — no account required.'}
              </p>
              <div className="mt-4 flex flex-wrap gap-3 text-xs text-stone-300">
                {dateLabel ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 backdrop-blur">
                    <CalendarDaysIcon className="h-4 w-4" aria-hidden />
                    {dateLabel}
                  </span>
                ) : null}
                {event.venue ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 backdrop-blur">
                    <MapPinIcon className="h-4 w-4" aria-hidden />
                    {event.venue}
                  </span>
                ) : null}
              </div>
            </div>
          </>
        ) : (
          <div className="mx-auto max-w-6xl px-4 py-8 sm:py-10">
            <h1 className="font-serif text-3xl tracking-tight text-stone-900 sm:text-4xl">
              {page.headline || event.name}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-stone-600">
              {page.description || event.description || 'Purchase tickets — no account required.'}
            </p>
            <p className="mt-2 text-xs text-stone-500">{[event.venue, dateLabel].filter(Boolean).join(' · ')}</p>
          </div>
        )}
      </header>

      {/* Highlights strip */}
      <div className="border-b border-stone-200/60 bg-white/60">
        <ul className="mx-auto flex max-w-6xl flex-wrap justify-center gap-4 px-4 py-3 sm:gap-8">
          {HIGHLIGHTS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-center gap-2 text-xs font-medium text-stone-600">
              <Icon className="h-4 w-4 text-amber-700" aria-hidden />
              {text}
            </li>
          ))}
        </ul>
      </div>

      <main className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-8 lg:grid-cols-[minmax(0,1fr)_380px] lg:gap-10">
        <div className="min-w-0 space-y-8">
          {page.whatHappens && (
            <section className="rounded-2xl border border-stone-200/80 bg-white/80 p-5 shadow-sm backdrop-blur-sm">
              <h2 className="font-serif text-xl text-stone-900">What happens</h2>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-stone-600">{page.whatHappens}</p>
            </section>
          )}

          {event.eventAgenda ? (
            <section className="rounded-2xl border border-stone-200/80 bg-white/80 p-5 shadow-sm backdrop-blur-sm">
              <h2 className="font-serif text-xl text-stone-900">Schedule / highlights</h2>
              <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-stone-600">{event.eventAgenda}</p>
            </section>
          ) : null}

          {/* Tickets — prominent */}
          <section id="tickets" className="scroll-mt-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <h2 className="font-serif text-2xl text-stone-900">Choose your tickets</h2>
                <p className="mt-1 text-sm text-stone-500">Select quantities — payment in the next step.</p>
              </div>
              <div className="rounded-xl bg-stone-900 px-4 py-2 text-right text-white shadow-md">
                <p className="text-[10px] uppercase tracking-wider text-stone-400">Remaining</p>
                <p className="text-xl font-bold tabular-nums">{remainingPool}</p>
                <p className="text-[10px] text-stone-400">of {totalCapacity} available</p>
              </div>
            </div>

            <ul className="mt-4 space-y-3">
              {types.map((t) => {
                const current = getQty(t.id);
                const others = totalSelected - current;
                const maxForType = Math.max(0, maxAvailable - others);
                const atMin = current <= 0;
                const atMax = current >= maxForType || !salesOpen || maxAvailable <= 0;
                const chips = [
                  t.kind !== 'NORMAL' ? t.kind.replace(/_/g, ' ') : null,
                  t.validDaysLabel,
                  t.transferable ? 'Transferable' : null,
                ].filter(Boolean);

                return (
                  <li
                    key={t.id}
                    className={`rounded-2xl border bg-white p-4 shadow-sm transition-shadow sm:p-5 ${
                      current > 0 ? 'border-amber-300/80 ring-1 ring-amber-200/50' : 'border-stone-200/80 hover:shadow-md'
                    }`}
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-lg font-semibold text-stone-900">{t.name}</p>
                          <span className="rounded-lg bg-gradient-to-r from-amber-100 to-amber-50 px-2.5 py-0.5 text-sm font-bold text-amber-900">
                            {catalogPrice(t.price, t.currency)}
                          </span>
                        </div>
                        {t.description ? (
                          <p className="mt-1 text-sm text-stone-600">{t.description}</p>
                        ) : null}
                        {chips.length ? (
                          <ul className="mt-2 flex flex-wrap gap-1.5">
                            {chips.map((chip) => (
                              <li
                                key={chip}
                                className="rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-stone-500"
                              >
                                {chip}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                      <TicketQuantityStepper
                        value={current}
                        label={t.name}
                        disabled={!salesOpen || maxAvailable <= 0}
                        atMin={atMin}
                        atMax={atMax}
                        onDecrement={() => adjustQty(t.id, -1)}
                        onIncrement={() => adjustQty(t.id, 1)}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>

            {totalSelected > 0 && remainingPool === 0 ? (
              <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                You&apos;ve selected the maximum number of tickets currently available.
              </p>
            ) : null}
          </section>

          {(page.sponsors || []).length > 0 && (
            <section>
              <h2 className="font-serif text-xl text-stone-900">Sponsors</h2>
              <ul className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {page.sponsors.map((s, i) => {
                  const logoUrl = mediaUrls[String(s.logoFileId || '').trim()];
                  return (
                    <li
                      key={s.sponsorshipId || s.name || i}
                      className="flex min-w-0 items-start gap-3 rounded-xl border border-stone-200/80 bg-white/80 p-4 shadow-sm"
                    >
                      {logoUrl ? (
                        <img
                          src={logoUrl}
                          alt={s.name ? `${s.name} logo` : 'Sponsor logo'}
                          className="h-12 w-12 shrink-0 rounded-lg border border-stone-200 bg-white object-contain p-1"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-12 w-12 shrink-0 rounded-lg border border-dashed border-stone-300 bg-stone-50" aria-hidden />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-stone-800">{s.name}</p>
                        {s.blurb ? <p className="text-xs text-stone-500">{s.blurb}</p> : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {(page.speakers || []).length > 0 && (
            <section>
              <h2 className="font-serif text-xl text-stone-900">Guest speakers</h2>
              <ul className="mt-3 space-y-3">
                {page.speakers.map((s, i) => {
                  const photoUrl = mediaUrls[String(s.photoFileId || '').trim()];
                  return (
                    <li key={s.name || i} className="flex gap-4 rounded-xl border border-stone-200/80 bg-white/80 p-4 shadow-sm">
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={s.name ? `${s.name} photo` : 'Speaker'}
                          className="h-16 w-16 shrink-0 rounded-full border-2 border-white object-cover shadow ring-1 ring-stone-200"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-16 w-16 shrink-0 rounded-full border border-dashed border-stone-300 bg-stone-50" aria-hidden />
                      )}
                      <div className="min-w-0">
                        <p className="font-medium text-stone-800">{s.name}</p>
                        {s.title ? <p className="text-xs font-medium text-amber-900/70">{s.title}</p> : null}
                        {s.bio ? <p className="mt-1 text-sm leading-relaxed text-stone-600">{s.bio}</p> : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}
        </div>

        {/* Checkout aside */}
        <aside className="h-fit lg:sticky lg:top-4">
          <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-lg">
            <div className="bg-gradient-to-r from-stone-900 to-stone-800 px-5 py-4 text-white">
              <p className="text-xs uppercase tracking-wider text-stone-400">Your order</p>
              <p className="mt-1 text-2xl font-bold tabular-nums">
                {lineTotal.count > 0 ? money(lineTotal.grand, lineTotal.currency) : '—'}
              </p>
              <p className="text-xs text-stone-400">
                {lineTotal.count > 0 ? `${lineTotal.count} ticket${lineTotal.count === 1 ? '' : 's'} selected` : 'No tickets selected yet'}
              </p>
            </div>

            <div className="p-5">
              <div className="flex gap-1 rounded-xl bg-stone-100 p-1">
                {[
                  { id: 'guest', label: 'Guest checkout' },
                  { id: 'company', label: 'Company bulk' },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setPurchaseMode(tab.id)}
                    className={`flex-1 rounded-lg px-2 py-2 text-xs font-medium transition ${
                      purchaseMode === tab.id ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {lineTotal.lines.length > 0 ? (
                <ul className="mt-4 space-y-2 border-b border-stone-100 pb-4">
                  {lineTotal.lines.map((line) => (
                    <li key={line.id} className="flex justify-between text-sm">
                      <span className="text-stone-600">{line.qty} × {line.name}</span>
                      <span className="font-medium tabular-nums text-stone-900">{money(line.lineAmount, line.currency)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {purchaseMode === 'guest' ? (
                <>
                  <p className="mt-4 text-xs text-stone-500">No registration needed — you&apos;ll receive a secure payment link.</p>
                  <form className="mt-4 space-y-3" onSubmit={checkout}>
                    <p className="text-[11px] text-stone-500"><span className="text-red-600">*</span> Required. Your details appear on the invoice and receipt, and all emails go to the address you enter.</p>
                    <Field label="Full name" required>
                      <input required autoComplete="name" className={INPUT_CLS} placeholder="Jane Perera" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} />
                    </Field>
                    <Field label="Email" required hint="Invoice, receipt & tickets are sent here">
                      <input required type="email" autoComplete="email" className={INPUT_CLS} placeholder="you@email.com" value={form.email} onChange={(e) => setForm((s) => ({ ...s, email: e.target.value }))} />
                    </Field>
                    <Field label="Phone">
                      <input type="tel" autoComplete="tel" className={INPUT_CLS} placeholder="+94 77 123 4567" value={form.phone} onChange={(e) => setForm((s) => ({ ...s, phone: e.target.value }))} />
                    </Field>
                    <div className="rounded-xl border border-stone-200 bg-stone-50/60 p-3 space-y-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">Invoice details</p>
                      <Field label="Company name" hint="Shown as the bill-to name">
                        <input className={INPUT_CLS} autoComplete="organization" placeholder="Leave blank to bill your own name" value={form.companyName} onChange={(e) => setForm((s) => ({ ...s, companyName: e.target.value }))} />
                      </Field>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label="V.A.T. registration no.">
                          <input className={INPUT_CLS} placeholder="e.g. 123456789-7000" value={form.companyVatNumber} onChange={(e) => setForm((s) => ({ ...s, companyVatNumber: e.target.value }))} />
                        </Field>
                        <Field label="TIN no.">
                          <input className={INPUT_CLS} placeholder="e.g. 123456789" value={form.companyTin} onChange={(e) => setForm((s) => ({ ...s, companyTin: e.target.value }))} />
                        </Field>
                      </div>
                      <Field label="Billing address">
                        <input autoComplete="street-address" className={INPUT_CLS} placeholder="Address for the invoice" value={form.billingAddress} onChange={(e) => setForm((s) => ({ ...s, billingAddress: e.target.value }))} />
                      </Field>
                    </div>
                    <PriceBreakdown lineTotal={lineTotal} calculating={calculating} />
                    <button
                      type="submit"
                      disabled={busy || !salesOpen || lineTotal.count < 1 || lineTotal.count > maxAvailable}
                      className="w-full rounded-xl bg-gradient-to-r from-amber-700 to-amber-800 px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:from-amber-800 hover:to-amber-900 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {busy ? 'Creating order…' : salesOpen ? 'Proceed to payment' : 'Sales closed'}
                    </button>
                  </form>
                  {result?.paymentUrl && (
                    <p className="mt-3 text-xs text-stone-500">
                      Payment page:{' '}
                      <Link className="font-medium text-amber-800 underline" to={`/public/event-ticket-payment/${result.paymentToken}`}>
                        Open
                      </Link>
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p className="mt-4 text-xs text-stone-500">Request a proforma — SLIM staff will review and contact you.</p>
                  <form className="mt-4 space-y-3" onSubmit={submitCompanyRequest}>
                    <p className="text-[11px] text-stone-500"><span className="text-red-600">*</span> Required. These details are used on the proforma, invoice and receipt, and all emails go to the coordinator email.</p>
                    <Field label="Company name" required>
                      <input required autoComplete="organization" className={INPUT_CLS} placeholder="Acme Pvt Ltd" value={companyForm.companyName} onChange={(e) => setCompanyForm((s) => ({ ...s, companyName: e.target.value }))} />
                    </Field>
                    <Field label="Coordinator name" required>
                      <input required autoComplete="name" className={INPUT_CLS} placeholder="Coordinator full name" value={companyForm.coordinatorName} onChange={(e) => setCompanyForm((s) => ({ ...s, coordinatorName: e.target.value }))} />
                    </Field>
                    <Field label="Coordinator email" required hint="Proforma, invoice & receipt are sent here">
                      <input required type="email" autoComplete="email" className={INPUT_CLS} placeholder="coordinator@company.com" value={companyForm.coordinatorEmail} onChange={(e) => setCompanyForm((s) => ({ ...s, coordinatorEmail: e.target.value }))} />
                    </Field>
                    <Field label="Coordinator phone">
                      <input type="tel" autoComplete="tel" className={INPUT_CLS} placeholder="+94 77 123 4567" value={companyForm.coordinatorPhone} onChange={(e) => setCompanyForm((s) => ({ ...s, coordinatorPhone: e.target.value }))} />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="V.A.T. registration no.">
                        <input className={INPUT_CLS} placeholder="e.g. 123456789-7000" value={companyForm.companyVatNumber} onChange={(e) => setCompanyForm((s) => ({ ...s, companyVatNumber: e.target.value }))} />
                      </Field>
                      <Field label="TIN no.">
                        <input className={INPUT_CLS} placeholder="e.g. 123456789" value={companyForm.companyTin} onChange={(e) => setCompanyForm((s) => ({ ...s, companyTin: e.target.value }))} />
                      </Field>
                    </div>
                    <Field label="Billing address">
                      <input autoComplete="street-address" className={INPUT_CLS} placeholder="Company billing address" value={companyForm.billingAddress} onChange={(e) => setCompanyForm((s) => ({ ...s, billingAddress: e.target.value }))} />
                    </Field>
                    <Field label="Notes">
                      <textarea className={INPUT_CLS} rows={2} placeholder="Invoice details, table preferences…" value={companyForm.notes} onChange={(e) => setCompanyForm((s) => ({ ...s, notes: e.target.value }))} />
                    </Field>
                    <PriceBreakdown lineTotal={lineTotal} calculating={calculating} />
                    <button
                      type="submit"
                      disabled={busy || !salesOpen || lineTotal.count < 1 || lineTotal.count > maxAvailable}
                      className="w-full rounded-xl bg-stone-900 px-4 py-3 text-sm font-semibold text-white hover:bg-stone-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {busy ? 'Submitting…' : salesOpen ? 'Submit request' : 'Sales closed'}
                    </button>
                  </form>
                  {companyResult?.requestNumber ? (
                    <p className="mt-3 rounded-xl bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                      Request <strong>{companyResult.requestNumber}</strong> received. We&apos;ll contact {companyForm.coordinatorEmail}.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
};

export default PublicEventTicketPurchasePage;
